import "./partner-landing.css";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";

declare global {
  interface Window {
    fpr?: (...args: unknown[]) => void;
  }
}

// ─── Types ───

interface OrbitItem {
  angle: number;
  radius: number;
  domain: string;
  alt: string;
  effect?: "soft" | "blur";
  hideMobile?: boolean;
}

interface OrbitRing {
  className: string;
  size: number;
  duration: string;
  reverse?: boolean;
  items: OrbitItem[];
}

interface ComparisonRow {
  icon: string;
  name: string;
  desc: string;
  replaces: Array<{ label: string; title: string }>;
  priceKey: string;
  isDynamic?: boolean;
}

interface FeatureTabDef {
  id: string;
  title: string;
  desc: string;
}

interface AdSpendEntry {
  firstName: string;
  totalSpend: number;
  currency: string;
}

interface AdSpendStats {
  entries: AdSpendEntry[];
  totalManaged: number;
  userCount: number;
  fetchedAt: string;
}

// ─── Constants ───

const FAVICON = (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
const PLN_UNIT = " z\u0142";
const VP_LOGO =
  "https://assets.cdn.filesafe.space/bVblz6CWD31c4iY9FJQr/media/69cb85210c5c89a989cb4afc.png";
const VP_PRICE = 2349;
const HOURLY_RATE = 80;
const TOP_AD_SPEND_COUNT = 3;

const ORBIT_RINGS: OrbitRing[] = [
  {
    className: "ring-inner",
    size: 620,
    duration: "42s",
    items: [
      { angle: 305, radius: 310, domain: "facebook.com", alt: "Meta Ads" },
      { angle: 332, radius: 310, domain: "pinterest.com", alt: "Pinterest Ads" },
      { angle: 356, radius: 310, domain: "linkedin.com", alt: "LinkedIn Ads" },
      { angle: 26, radius: 310, domain: "ads.google.com", alt: "Google Ads" },
      { angle: 56, radius: 310, domain: "shopify.com", alt: "Shopify" },
      {
        angle: 133,
        radius: 310,
        domain: "tiktok.com",
        alt: "TikTok Ads",
        effect: "soft",
        hideMobile: true,
      },
      { angle: 215, radius: 310, domain: "instagram.com", alt: "Instagram", effect: "soft" },
    ],
  },
  {
    className: "ring-mid",
    size: 820,
    duration: "56s",
    reverse: true,
    items: [
      { angle: 318, radius: 410, domain: "woocommerce.com", alt: "WooCommerce", effect: "soft" },
      { angle: 350, radius: 410, domain: "slack.com", alt: "Slack" },
      { angle: 22, radius: 410, domain: "analytics.google.com", alt: "Google Analytics" },
      { angle: 52, radius: 410, domain: "n8n.io", alt: "n8n" },
      {
        angle: 100,
        radius: 410,
        domain: "tiktok.com",
        alt: "TikTok Ads",
        effect: "soft",
        hideMobile: true,
      },
      { angle: 165, radius: 410, domain: "baselinker.com", alt: "BaseLinker" },
      { angle: 208, radius: 410, domain: "instagram.com", alt: "Instagram" },
      {
        angle: 240,
        radius: 410,
        domain: "allegro.pl",
        alt: "Allegro Ads",
        effect: "soft",
        hideMobile: true,
      },
    ],
  },
  {
    className: "ring-outer",
    size: 1080,
    duration: "74s",
    items: [
      {
        angle: 286,
        radius: 540,
        domain: "shopify.com",
        alt: "Shopify",
        effect: "blur",
        hideMobile: true,
      },
      {
        angle: 332,
        radius: 540,
        domain: "youtube.com",
        alt: "YouTube",
        effect: "blur",
        hideMobile: true,
      },
      {
        angle: 26,
        radius: 540,
        domain: "linkedin.com",
        alt: "LinkedIn Ads",
        effect: "blur",
        hideMobile: true,
      },
      {
        angle: 76,
        radius: 540,
        domain: "slack.com",
        alt: "Slack",
        effect: "blur",
        hideMobile: true,
      },
      {
        angle: 122,
        radius: 540,
        domain: "pinterest.com",
        alt: "Pinterest Ads",
        effect: "blur",
        hideMobile: true,
      },
      {
        angle: 196,
        radius: 540,
        domain: "facebook.com",
        alt: "Meta Ads",
        effect: "blur",
        hideMobile: true,
      },
    ],
  },
];

const FEATURE_TABS: FeatureTabDef[] = [
  { id: "optimize", title: "featureTabs.optimize.title", desc: "featureTabs.optimize.desc" },
  {
    id: "create",
    title: "Ad Creator",
    desc: "Generuj kreacje, kopie i warianty A/B w kilka sekund",
  },
  {
    id: "analyze",
    title: "Analityka",
    desc: "Cross-platform dashboard z danymi z Meta, Google i e-commerce",
  },
  { id: "automate", title: "featureTabs.automate.title", desc: "featureTabs.automate.desc" },
  { id: "scale", title: "featureTabs.scale.title", desc: "featureTabs.scale.desc" },
];

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    icon: "📊",
    name: "compRows.tracking.name",
    desc: "compRows.tracking.desc",
    replaces: [{ label: "Hy", title: "Hyros" }],
    priceKey: "tracking",
    isDynamic: true,
  },
  {
    icon: "🧠",
    name: "compRows.analyst.name",
    desc: "compRows.analyst.desc",
    replaces: [{ label: "👤", title: "Ad Specialist" }],
    priceKey: "analyst",
    isDynamic: true,
  },
  {
    icon: "✍️",
    name: "Ad Creator — Copywriting",
    desc: "AI generuje kopie, headliny, CTA, warianty A/B",
    replaces: [{ label: "👤", title: "Copywriter" }],
    priceKey: "copy",
    isDynamic: true,
  },
  {
    icon: "🎨",
    name: "Ad Creator — Grafiki",
    desc: "AI tworzy bannery i kreacje statyczne pod formaty platform",
    replaces: [{ label: "👤", title: "Grafik" }],
    priceKey: "graphic",
    isDynamic: true,
  },
  {
    icon: "🎬",
    name: "compRows.video.name",
    desc: "compRows.video.desc",
    replaces: [{ label: "👤", title: "Video Editor" }],
    priceKey: "video",
    isDynamic: true,
  },
  {
    icon: "⏱️",
    name: "compRows.manual.name",
    desc: "compRows.manual.desc",
    replaces: [
      { label: "⏳", title: "Your Time" },
      { label: "👤", title: "Assistant" },
    ],
    priceKey: "manual",
    isDynamic: true,
  },
  {
    icon: "📄",
    name: "compRows.reports.name",
    desc: "compRows.reports.desc",
    replaces: [
      { label: "⏳", title: "Your Time" },
      { label: "LS", title: "Looker Studio" },
    ],
    priceKey: "reports",
    isDynamic: true,
  },
  {
    icon: "📋",
    name: "compRows.mgmt.name",
    desc: "compRows.mgmt.desc",
    replaces: [{ label: "⏳", title: "Your Time" }],
    priceKey: "mgmt",
    isDynamic: true,
  },
  {
    icon: "💡",
    name: "compRows.content.name",
    desc: "compRows.content.desc",
    replaces: [
      { label: "No", title: "Notion" },
      { label: "GP", title: "ChatGPT Pro" },
    ],
    priceKey: "content",
  },
  {
    icon: "👥",
    name: "Multi-client & role",
    desc: "Panel klienta, uprawnienia, whitelabel raporty",
    replaces: [
      { label: "AA", title: "AgencyAnalytics" },
      { label: "Wh", title: "Whatagraph" },
    ],
    priceKey: "multiclient",
  },
  {
    icon: "⚡",
    name: "Workflow & automatyzacje",
    desc: "Alerty, auto-raporty, webhooks, n8n",
    replaces: [
      { label: "Za", title: "Zapier" },
      { label: "Ma", title: "Make" },
    ],
    priceKey: "automations",
  },
];

const INTEGRATION_LIST = [
  { name: "Meta Ads", active: true },
  { name: "Google Ads", active: true },
  { name: "Shopify", active: true },
  { name: "YouTube", active: true },
  { name: "Instagram", active: true },
  { name: "TikTok Ads", active: true },
  { name: "Allegro Ads", active: true },
  { name: "WooCommerce", active: true },
  { name: "n8n", active: true },
  { name: "Google Analytics", active: true },
  { name: "Slack", active: true },
  { name: "Pinterest Ads", active: false },
  { name: "LinkedIn Ads", active: false },
  { name: "Baselinker", active: false },
];

const VALUE_FEATURES = [
  { title: "valueFeatures.singleSource.title", desc: "valueFeatures.singleSource.desc" },
  { title: "valueFeatures.aiCreatives.title", desc: "valueFeatures.aiCreatives.desc" },
  { title: "valueFeatures.autoReports.title", desc: "valueFeatures.autoReports.desc" },
  { title: "valueFeatures.rolesPermissions.title", desc: "valueFeatures.rolesPermissions.desc" },
];

const USE_CASES = [
  {
    badge: "Lead Gen",
    title: "Lead Generation",
    desc: "useCases.leadGen.desc",
    tags: ["Meta Ads", "Google Ads", "CRM"],
    link: "https://viral.partners/integracje/leadconnector",
  },
  {
    badge: "E-com",
    title: "E-commerce",
    desc: "useCases.ecom.desc",
    tags: ["Shopify", "Allegro", "Catalog"],
    link: "https://viral.partners/cennik",
  },
  {
    badge: "Content",
    title: "Content Creation",
    desc: "useCases.content.desc",
    tags: ["Ad Creator", "Instagram", "TikTok"],
    link: "https://viral.partners/funkcje/ai-copywriter",
  },
  {
    badge: "Agency",
    title: "useCases.agency.title",
    desc: "useCases.agency.desc",
    tags: ["Multi-client", "Reports", "Team"],
    link: "https://viral.partners/cennik",
  },
];

// ─── Helpers ───

function buildAppUrl(): string {
  const searchParams = new URLSearchParams(window.location.search);
  const fprParam = searchParams.get("fpr");
  return fprParam
    ? `https://app.viral.partners?fpr=${encodeURIComponent(fprParam)}`
    : "https://app.viral.partners";
}

function formatPln(n: number): string {
  return Math.round(n).toLocaleString("pl-PL").replace(/,/g, " ") + PLN_UNIT;
}

function calculatePrices(accounts: number, adsPerAccount: number): Record<string, number> {
  const totalAds = accounts * adsPerAccount;
  const rate = HOURLY_RATE;

  return {
    tracking: accounts * 1000,
    analyst: Math.ceil(accounts / 10) * 8000,
    copy: totalAds * 100,
    graphic: totalAds * 40,
    video: totalAds * 100,
    manual: totalAds * (5 / 60) * rate,
    reports: accounts * (10 / 60) * rate * 22,
    mgmt: accounts * (20 / 60) * rate * 22,
    content: 250,
    multiclient: accounts <= 3 ? 400 : 600 + (accounts - 3) * 80,
    automations: 200,
  };
}

// ─── Nav ───

function PartnerNav({ onCTAClick }: { onCTAClick: () => void }) {
  const { t } = useTranslation("partner");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const appUrl = buildAppUrl();

  const toggleMobile = useCallback(() => {
    setMobileOpen((prev) => {
      document.body.style.overflow = !prev ? "hidden" : "";
      return !prev;
    });
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    document.body.style.overflow = "";
  }, []);

  const handleDropdownClick = useCallback((id: string, e: React.MouseEvent) => {
    if (window.innerWidth <= 768) {
      e.preventDefault();
      setOpenDropdown((prev) => (prev === id ? null : id));
    }
  }, []);

  return (
    <nav ref={navRef} className={mobileOpen ? "mob-open" : ""}>
      <div className="container">
        <a
          href="https://viral.partners/strona-gwna"
          className="nav-logo"
          aria-label="Viral Partners"
        >
          <img src={VP_LOGO} alt="Viral Partners logo" />
        </a>

        <button className="nav-hamburger" aria-label="Menu" onClick={toggleMobile}>
          {!mobileOpen ? (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
        </button>

        <div className="nav-links">
          {/* Funkcje dropdown */}
          <div className={`nav-int-wrap ${openDropdown === "funkcje" ? "mob-open" : ""}`}>
            <a
              href="https://viral.partners/#features"
              className="nav-int-trigger"
              onClick={(e) => handleDropdownClick("funkcje", e)}
            >
              {t("nav.features")}
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 4l4 4 4-4" />
              </svg>
            </a>
            <div className="nav-int-dropdown">
              <div className="dd-header">{t("nav.featuresHeader")}</div>
              <div className="dd-grid">
                <a
                  href="https://viral.partners/funkcje/kreator-kampanii"
                  className="dd-item"
                  onClick={closeMobile}
                >
                  <div className="dd-icon" style={{ background: "rgba(230,49,38,0.12)" }}>
                    🚀
                  </div>
                  <div className="dd-item-text">
                    <strong>{t("nav.campaignCreatorTitle")}</strong>
                    <span>{t("nav.campaignCreatorDesc")}</span>
                  </div>
                </a>
                <a
                  href="https://viral.partners/funkcje/ai-copywriter"
                  className="dd-item"
                  onClick={closeMobile}
                >
                  <div className="dd-icon" style={{ background: "rgba(139,92,246,0.12)" }}>
                    ✍️
                  </div>
                  <div className="dd-item-text">
                    <strong>AI Copywriter</strong>
                    <span>{t("nav.aiCopywriterDesc")}</span>
                  </div>
                </a>
                <a
                  href="https://viral.partners/funkcje/monitoring-wynikow"
                  className="dd-item"
                  onClick={closeMobile}
                >
                  <div className="dd-icon" style={{ background: "rgba(16,185,129,0.12)" }}>
                    📈
                  </div>
                  <div className="dd-item-text">
                    <strong>{t("nav.monitoringTitle")}</strong>
                    <span>{t("nav.monitoringDesc")}</span>
                  </div>
                </a>
                <a
                  href="https://viral.partners/funkcje/dashboard-klientow"
                  className="dd-item"
                  onClick={closeMobile}
                >
                  <div className="dd-icon" style={{ background: "rgba(59,130,246,0.12)" }}>
                    📊
                  </div>
                  <div className="dd-item-text">
                    <strong>{t("nav.clientDashTitle")}</strong>
                    <span>{t("nav.clientDashDesc")}</span>
                  </div>
                </a>
              </div>
              <div className="dd-divider" />
              <div className="dd-footer">
                <a href="https://viral.partners/#features">{t("nav.seeAllFeatures")}</a>
              </div>
            </div>
          </div>

          {/* Integracje dropdown */}
          <div className={`nav-int-wrap ${openDropdown === "integracje" ? "mob-open" : ""}`}>
            <a
              href="https://viral.partners/#integrations"
              className="nav-int-trigger"
              onClick={(e) => handleDropdownClick("integracje", e)}
            >
              {t("nav.integrations")}
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 4l4 4 4-4" />
              </svg>
            </a>
            <div className="nav-int-dropdown">
              <div className="dd-header">{t("nav.activeIntegrationsHeader")}</div>
              <div className="dd-grid">
                <a
                  href="https://viral.partners/integracje/meta-ads"
                  className="dd-item"
                  onClick={closeMobile}
                >
                  <div className="dd-icon" style={{ background: "rgba(24,119,242,0.12)" }}>
                    📘
                  </div>
                  <div className="dd-item-text">
                    <strong>Meta Ads</strong>
                    <span>Facebook &amp; Instagram Ads</span>
                  </div>
                </a>
                <a
                  href="https://viral.partners/integracje/leadconnector"
                  className="dd-item"
                  onClick={closeMobile}
                >
                  <div className="dd-icon" style={{ background: "rgba(255,163,0,0.12)" }}>
                    🚀
                  </div>
                  <div className="dd-item-text">
                    <strong>LeadConnector</strong>
                    <span>{t("nav.lcDesc")}</span>
                  </div>
                </a>
                <a
                  href="https://viral.partners/integracje/google-sheets"
                  className="dd-item"
                  onClick={closeMobile}
                >
                  <div className="dd-icon" style={{ background: "rgba(15,157,88,0.12)" }}>
                    📊
                  </div>
                  <div className="dd-item-text">
                    <strong>Google Sheets</strong>
                    <span>{t("nav.gsDesc")}</span>
                  </div>
                </a>
              </div>
              <div className="dd-divider" />
              <div className="dd-footer">
                <a href="https://viral.partners/#integrations">{t("nav.seeAllIntegrations")}</a>
              </div>
            </div>
          </div>

          <a href="https://viral.partners/cennik" onClick={closeMobile}>
            {t("nav.pricing")}
          </a>
          <a href={`${appUrl}/register`} className="nav-cta" onClick={closeMobile}>
            {t("nav.tryForFree")}
          </a>
          <a
            href={appUrl}
            className="nav-login"
            onClick={closeMobile}
            style={{
              color: "rgba(255,255,255,0.7)",
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 500,
              border: "1px solid rgba(255,255,255,0.08)",
              padding: "9px 18px",
              borderRadius: 6,
              transition: "all 0.2s",
            }}
          >
            {t("nav.login")}
          </a>
        </div>
      </div>
    </nav>
  );
}

// ─── Hero ───

function HeroSection() {
  const { t } = useTranslation("partner");
  const appUrl = buildAppUrl();

  return (
    <section className="hero">
      <div className="hero-visual" aria-hidden="true">
        <div className="hero-orbits">
          {ORBIT_RINGS.map((ring) => (
            <div
              key={ring.className}
              className={`hero-ring ${ring.className} ${ring.reverse ? "reverse" : ""}`}
              style={{ "--size": ring.size, "--duration": ring.duration } as React.CSSProperties}
            >
              {ring.items.map((item) => (
                <div
                  key={`${ring.className}-${item.angle}`}
                  className={`orbit-item ${item.hideMobile ? "hide-mobile" : ""}`}
                  style={{ "--angle": item.angle, "--radius": item.radius } as React.CSSProperties}
                >
                  <div className={`hero-float ${item.effect ? `is-${item.effect}` : ""}`}>
                    <img src={FAVICON(item.domain)} alt={item.alt} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="container">
        <div className="hero-content">
          <p
            className="fade-up"
            style={{
              fontSize: "1.1rem",
              letterSpacing: ".15em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,.55)",
              marginBottom: ".5rem",
            }}
          >
            Viral Partners
          </p>
          <h1 className="fade-up fd1">{t("hero.title")}</h1>
          <p className="hero-sub fade-up fd2">{t("hero.subtitle")}</p>
          <div className="hero-buttons fade-up fd3">
            <a href={`${appUrl}/register`} className="btn-primary">
              {t("hero.tryForFree")}
            </a>
            <a href="https://viral.partners/strona-gwna#features" className="btn-secondary">
              {t("hero.seePlatform")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Proof Bar ───

function ProofBar() {
  const { t } = useTranslation("partner");
  return (
    <section className="proof-bar">
      <div className="container" style={{ gridTemplateColumns: "1fr", maxWidth: 720 }}>
        <div className="proof-item">
          <p className="proof-quote">{t("proof.quote")}</p>
          <p className="proof-author">
            <strong>{t("proof.author")}</strong>
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Value Section ───

function ValueSection() {
  const { t } = useTranslation("partner");
  return (
    <section className="value-section" id="features">
      <div className="container">
        <div className="value-grid">
          <div className="value-heading">
            <div className="label">{t("value.label")}</div>
            <h2>
              {t("value.titlePre")} <span>{t("value.titleHighlight")}</span> {t("value.titlePost")}
            </h2>
            <p className="value-desc">{t("value.subtitle")}</p>
          </div>
          <div className="value-features">
            {VALUE_FEATURES.map((f) => (
              <div className="vf" key={f.title}>
                <div className="vf-icon">✓</div>
                <div>
                  <h4>{t(f.title)}</h4>
                  <p>{t(f.desc)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Use Cases ───

function UseCasesSection() {
  const { t } = useTranslation("partner");
  return (
    <section className="usecases" id="usecases">
      <div className="container">
        <div className="usecases-header">
          <div className="label">{t("useCases.label")}</div>
          <h2>{t("useCases.title")}</h2>
          <p>{t("useCases.subtitle")}</p>
        </div>
        <div className="usecases-grid">
          {USE_CASES.map((uc) => (
            <div className="uc" key={uc.badge}>
              <div className="uc-badge">{uc.badge}</div>
              <h3>{t(uc.title)}</h3>
              <p>{t(uc.desc)}</p>
              <div className="uc-tags">
                {uc.tags.map((t) => (
                  <span className="uc-tag" key={t}>
                    {t}
                  </span>
                ))}
              </div>
              <a href={uc.link} className="uc-link">
                {t("useCases.learnMore")}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Features Deep (Tabs) ───

function FeaturesDeepSection() {
  const { t } = useTranslation("partner");
  const [activeTab, setActiveTab] = useState("optimize");

  return (
    <section className="features-deep">
      <div className="container">
        <div className="features-header">
          <div className="label">{t("features.label")}</div>
          <h2>
            {t("features.titleLine1")}
            <br />
            {t("features.titleLine2")}
          </h2>
          <p>{t("features.subtitle")}</p>
        </div>
        <div className="features-tabs">
          <div className="features-nav">
            {FEATURE_TABS.map((tab) => (
              <div
                key={tab.id}
                className={`fn ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <h4>{t(tab.title)}</h4>
                <p>{t(tab.desc)}</p>
              </div>
            ))}
          </div>
          <div className="features-display">
            <FeaturePanel id="optimize" active={activeTab === "optimize"}>
              <h3>
                {t("features.optimize.h3Pre")} <span>{t("features.optimize.h3Highlight")}</span>{" "}
                {t("features.optimize.h3Post")}
              </h3>
              <p>{t("features.optimize.p")}</p>
              <div className="fp-metrics">
                <div className="fp-metric">
                  <div className="fp-metric-val green">+32%</div>
                  <div className="fp-metric-label">{t("features.optimize.metric1")}</div>
                </div>
                <div className="fp-metric">
                  <div className="fp-metric-val">4.2x</div>
                  <div className="fp-metric-label">{t("features.optimize.metric2")}</div>
                </div>
                <div className="fp-metric">
                  <div className="fp-metric-val">-40%</div>
                  <div className="fp-metric-label">{t("features.optimize.metric3")}</div>
                </div>
              </div>
            </FeaturePanel>

            <FeaturePanel id="create" active={activeTab === "create"}>
              <h3>
                {t("features.create.h3Pre")} <span>{t("features.create.h3Highlight")}</span>{" "}
                {t("features.create.h3Post")}
              </h3>
              <p>{t("features.create.p")}</p>
              <div className="fp-cards">
                <div className="fp-card">
                  <div className="fp-card-icon">✎</div>
                  <div className="fp-card-text">
                    <strong>Kopie reklamowe</strong>Headliny, opisy, CTA
                  </div>
                </div>
                <div className="fp-card">
                  <div className="fp-card-icon">⚙</div>
                  <div className="fp-card-text">
                    <strong>{t("features.create.abTitle")}</strong>
                    {t("features.create.abDesc")}
                  </div>
                </div>
                <div className="fp-card">
                  <div className="fp-card-icon">▶</div>
                  <div className="fp-card-text">
                    <strong>Video scripts</strong>Skrypty pod UGC i Reels
                  </div>
                </div>
                <div className="fp-card">
                  <div className="fp-card-icon">★</div>
                  <div className="fp-card-text">
                    <strong>Brand voice</strong>Dopasowany ton marki
                  </div>
                </div>
              </div>
            </FeaturePanel>

            <FeaturePanel id="analyze" active={activeTab === "analyze"}>
              <h3>
                {t("features.analyze.h3Pre")} <span>{t("features.analyze.h3Highlight")}</span>{" "}
                {t("features.analyze.h3Post")}
              </h3>
              <p>{t("features.analyze.p")}</p>
              <div className="fp-metrics">
                <div className="fp-metric">
                  <div className="fp-metric-val">LTV</div>
                  <div className="fp-metric-label">{t("features.analyze.metric1")}</div>
                </div>
                <div className="fp-metric">
                  <div className="fp-metric-val">ROAS</div>
                  <div className="fp-metric-label">{t("features.analyze.metric2")}</div>
                </div>
                <div className="fp-metric">
                  <div className="fp-metric-val">CPA</div>
                  <div className="fp-metric-label">{t("features.analyze.metric3")}</div>
                </div>
              </div>
            </FeaturePanel>

            <FeaturePanel id="automate" active={activeTab === "automate"}>
              <h3>
                {t("features.automate.h3Pre")} <span>{t("features.automate.h3Highlight")}</span>{" "}
                {t("features.automate.h3Post")}
              </h3>
              <p>{t("features.automate.p")}</p>
              <div className="fp-cards">
                <div className="fp-card">
                  <div className="fp-card-icon">✉</div>
                  <div className="fp-card-text">
                    <strong>{t("features.automate.reportsTitle")}</strong>
                    {t("features.automate.reportsDesc")}
                  </div>
                </div>
                <div className="fp-card">
                  <div className="fp-card-icon">⚠</div>
                  <div className="fp-card-text">
                    <strong>{t("features.automate.alertsTitle")}</strong>
                    {t("features.automate.alertsDesc")}
                  </div>
                </div>
                <div className="fp-card">
                  <div className="fp-card-icon">⇄</div>
                  <div className="fp-card-text">
                    <strong>Sync danych</strong>n8n workflows
                  </div>
                </div>
                <div className="fp-card">
                  <div className="fp-card-icon">⚡</div>
                  <div className="fp-card-text">
                    <strong>Webhooks</strong>Custom integracje
                  </div>
                </div>
              </div>
            </FeaturePanel>

            <FeaturePanel id="scale" active={activeTab === "scale"}>
              <h3>
                {t("features.scale.h3Pre")} <span>{t("features.scale.h3Highlight")}</span>{" "}
                {t("features.scale.h3Post")}
              </h3>
              <p>{t("features.scale.p")}</p>
              <div className="fp-metrics">
                <div className="fp-metric">
                  <div className="fp-metric-val">∞</div>
                  <div className="fp-metric-label">{t("features.scale.metric1")}</div>
                </div>
                <div className="fp-metric">
                  <div className="fp-metric-val">4</div>
                  <div className="fp-metric-label">{t("features.scale.metric2")}</div>
                </div>
                <div className="fp-metric">
                  <div className="fp-metric-val green">SSO</div>
                  <div className="fp-metric-label">{t("features.scale.metric3")}</div>
                </div>
              </div>
            </FeaturePanel>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturePanel({
  id,
  active,
  children,
}: {
  id: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`fp ${active ? "active" : ""}`} id={`panel-${id}`}>
      {children}
    </div>
  );
}

// ─── Integrations ───

function IntegrationsSection() {
  const { t } = useTranslation("partner");
  return (
    <section className="integrations" id="integrations">
      <div className="container">
        <div className="label">{t("integrations.label")}</div>
        <h2>
          {t("integrations.titlePre")} <span>{t("integrations.titleHighlight")}</span>{" "}
          {t("integrations.titlePost")}
        </h2>
        <p className="integrations-sub">{t("integrations.subtitle")}</p>
        <div className="int-grid">
          {INTEGRATION_LIST.map((item) => (
            <div key={item.name} className={`ic ${item.active ? "" : "soon"}`}>
              <span className="ic-dot" />
              {item.name}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Comparison Section (with calculator) ───

function ComparisonSection() {
  const { t } = useTranslation("partner");
  const [accounts, setAccounts] = useState(3);
  const [adsPerAccount, setAdsPerAccount] = useState(15);
  const appUrl = buildAppUrl();

  const prices = useMemo(() => calculatePrices(accounts, adsPerAccount), [accounts, adsPerAccount]);

  const totalCost = useMemo(() => {
    return Object.values(prices).reduce((sum, val) => sum + val, 0);
  }, [prices]);

  const saved = totalCost - VP_PRICE;
  const savedPct = ((saved / totalCost) * 100).toFixed(1);
  const totalAds = accounts * adsPerAccount;

  return (
    <section className="comparison" id="comparison">
      <div className="container">
        <div className="comparison-header">
          <div className="label">{t("comparison.label")}</div>
          <h2>
            {t("comparison.titlePre")} <span>{t("comparison.titleHighlight")}</span>{" "}
            {t("comparison.titlePost")}
          </h2>
          <p>{t("comparison.subtitle")}</p>
        </div>

        {/* Calculator */}
        <div className="calc-panel">
          <div className="calc-title">
            <div className="calc-title-icon">⚙</div>
            {t("comparison.calcTitle")}
          </div>
          <div className="calc-row">
            <div className="calc-field">
              <label>{t("comparison.calcAccounts")}</label>
              <input
                type="range"
                min={1}
                max={20}
                value={accounts}
                step={1}
                onChange={(e) => setAccounts(Number(e.target.value))}
              />
              <div className="calc-val">
                {accounts} <small>{t("comparison.calcAccountsUnit")}</small>
              </div>
            </div>
            <div className="calc-field">
              <label>{t("comparison.calcAdsLabel")}</label>
              <input
                type="range"
                min={5}
                max={100}
                value={adsPerAccount}
                step={5}
                onChange={(e) => setAdsPerAccount(Number(e.target.value))}
              />
              <div className="calc-val">
                {adsPerAccount} <small>{t("comparison.calcAdsUnit")}</small>
              </div>
            </div>
          </div>
          <div className="calc-summary">
            <div className="calc-summary-left">
              {t("comparison.calcSummary", { ads: totalAds, accounts })}
            </div>
            <div className="calc-summary-right">{t("comparison.calcSummaryRight")}</div>
          </div>
        </div>

        {/* Table */}
        <table className="comp-table">
          <thead>
            <tr>
              <th>{t("comparison.colFeature")}</th>
              <th>{t("comparison.colReplaces")}</th>
              <th>{t("comparison.colCost")}</th>
              <th>
                <span className="comp-vp-head">Viral Partners</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON_ROWS.map((row) => (
              <tr key={row.priceKey}>
                <td>
                  <div className="comp-feature-name">
                    <div className="comp-feature-icon">{row.icon}</div>
                    <div className="comp-feature-text">
                      <strong>{t(row.name)}</strong>
                      <span>{t(row.desc)}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="comp-replaces">
                    {row.replaces.map((r) => (
                      <div key={r.label} className="comp-replaces-icon" title={r.title}>
                        {r.label}
                      </div>
                    ))}
                  </div>
                </td>
                <td className={`comp-price-cell ${row.isDynamic ? "is-dynamic" : ""}`}>
                  {formatPln(prices[row.priceKey])}
                </td>
                <td>
                  <span className="comp-check">✓</span>
                </td>
              </tr>
            ))}
            {/* Total row */}
            <tr className="comp-total-row">
              <td />
              <td className="comp-total-label">{t("comparison.totalLabel")}</td>
              <td className="comp-total-old">{formatPln(totalCost)}</td>
              <td className="comp-total-vp">
                {t("comparison.vpPrice")}
                <br />
                <small>{t("comparison.vpPriceNote")}</small>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="comp-savings">
          {t("comparison.savings", { amount: formatPln(saved), pct: savedPct })}
        </div>

        <div className="comp-cta-wrap">
          <a href={`${appUrl}/register`} className="btn-primary">
            {t("comparison.ctaBtn")}
          </a>
        </div>
        <p className="comp-note">{t("comparison.note")}</p>
      </div>
    </section>
  );
}

// ─── CTA Section ───

function CTASection() {
  const { t } = useTranslation("partner");
  const appUrl = buildAppUrl();

  return (
    <section className="cta-section" id="cta">
      <div className="container">
        <div className="label">{t("cta.label")}</div>
        <h2>
          {t("cta.titlePre")}
          <br />
          {t("cta.titlePre2")} <span>{t("cta.titleHighlight")}</span>.
        </h2>
        <p>{t("cta.subtitle")}</p>
        <div className="cta-buttons">
          <a href={`${appUrl}/register`} className="btn-primary">
            Przetestuj za darmo
          </a>
          <a href={appUrl} className="btn-secondary">
            {t("cta.login")}
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── Ad Spend Stats (social proof) ───

function AdSpendStatsSection() {
  const { data: stats } = useQuery<AdSpendStats>({
    queryKey: ["public-ad-spend-stats"],
    queryFn: async () => {
      const res = await fetch("/api/public/stats/ad-spend");
      if (!res.ok) throw new Error("Failed to fetch ad spend stats");
      return res.json();
    },
    staleTime: 4 * 60 * 60 * 1000,
    retry: 1,
  });

  const { t } = useTranslation("partner");
  if (!stats || stats.entries.length === 0) return null;

  const topEntries = stats.entries.slice(0, TOP_AD_SPEND_COUNT);
  const topTotal = topEntries.reduce((sum, e) => sum + e.totalSpend, 0);

  return (
    <section className="ad-spend-stats">
      <div className="container">
        <div className="ad-spend-box">
          <div className="ad-spend-label">{t("adSpend.label", { count: TOP_AD_SPEND_COUNT })}</div>
          <div className="ad-spend-entries">
            {topEntries.map((entry, i) => (
              <div key={i} className="ad-spend-entry">
                <div className="rank">#{i + 1}</div>
                <div className="amount">{formatPln(entry.totalSpend)}</div>
              </div>
            ))}
          </div>
          <div className="ad-spend-total">
            {t("adSpend.total", { amount: formatPln(topTotal) })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───

function PartnerFooter() {
  const { t } = useTranslation("partner");
  return (
    <footer>
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <a
              href="https://viral.partners/strona-gwna"
              className="nav-logo"
              aria-label="Viral Partners"
            >
              <img src={VP_LOGO} alt="Viral Partners logo" />
            </a>
            <p>{t("footer.tagline")}</p>
          </div>
          <div className="footer-col">
            <h4>{t("footer.product")}</h4>
            <a href="https://viral.partners/strona-gwna#features">{t("footer.features")}</a>
            <a href="https://viral.partners/cennik">{t("footer.pricing")}</a>
            <a href="https://viral.partners/strona-gwna#integrations">{t("footer.integrations")}</a>
            <a href="https://changelog.viral.partners">{t("footer.changelog")}</a>
          </div>
          <div className="footer-col">
            <h4>{t("footer.resources")}</h4>
            <a href="https://viral.partners/strona-gwna#features">{t("footer.docs")}</a>
            <a href="https://viral.partners/strona-gwna#features">API Reference</a>
            <a href="https://viral.partners/strona-gwna">Blog</a>
            <a href="https://viral.partners/cennik">Case Studies</a>
          </div>
          <div className="footer-col">
            <h4>{t("footer.company")}</h4>
            <a href="https://viral.partners/strona-gwna">{t("footer.about")}</a>
            <a href="mailto:maciej@viral.partners">{t("footer.contact")}</a>
            <a href="https://viral.partners/regulamin">{t("footer.terms")}</a>
            <a href="https://viral.partners/polityka-prywatnosci">{t("footer.privacyPolicy")}</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 Viral Partners · VP Holding PSA</span>
          <span>{t("footer.builtIn")}</span>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ───

export default function PartnerPage() {
  const [matched, params] = useRoute("/partner/:name");
  const partnerName = matched ? decodeURIComponent(params?.name || "") : "";
  const ctaRef = useRef<HTMLElement>(null);

  // FirstPromoter click tracking
  useEffect(() => {
    if (window.fpr) {
      window.fpr("click");
    }
  }, []);

  // Allow scrolling on this page
  useEffect(() => {
    document.documentElement.classList.add("allow-scroll");
    return () => {
      document.documentElement.classList.remove("allow-scroll");
    };
  }, []);

  const scrollToCTA = useCallback(() => {
    ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className="partner-landing">
      <PartnerNav onCTAClick={scrollToCTA} />
      <HeroSection />
      <ProofBar />
      <div className="sep" />
      <ValueSection />
      <UseCasesSection />
      <FeaturesDeepSection />
      <div className="sep" />
      <IntegrationsSection />
      <div className="sep" />
      <ComparisonSection />
      <CTASection />
      <AdSpendStatsSection />
      <div className="sep" />
      <PartnerFooter />
    </div>
  );
}
